import { useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import './PhotoViewer.css';

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const DOUBLE_TAP_MS = 280;
const DOUBLE_TAP_SCALE = 2.5;

export function PhotoViewer({ src, onClose }: { src: string; onClose: () => void }) {
  const stageRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const [animating, setAnimating] = useState(false);

  const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const pinchRef = useRef<{ dist: number; scale: number; tx: number; ty: number } | null>(null);
  const dragRef = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);
  const lastTapRef = useRef(0);
  const movedRef = useRef(false);

  const reset = () => {
    setAnimating(true);
    setScale(1);
    setTx(0);
    setTy(0);
  };

  // Bloqueamos el scroll del body mientras está abierto el visor
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    movedRef.current = false;
    setAnimating(false);

    if (pointersRef.current.size === 2) {
      const pts = Array.from(pointersRef.current.values());
      const dx = pts[0].x - pts[1].x;
      const dy = pts[0].y - pts[1].y;
      pinchRef.current = { dist: Math.hypot(dx, dy) || 1, scale, tx, ty };
      dragRef.current = null;
      return;
    }

    if (pointersRef.current.size === 1) {
      // Doble-tap: alterna 1x ↔ DOUBLE_TAP_SCALE
      const now = Date.now();
      if (now - lastTapRef.current < DOUBLE_TAP_MS) {
        if (scale > 1.05) {
          reset();
        } else {
          setAnimating(true);
          setScale(DOUBLE_TAP_SCALE);
        }
        lastTapRef.current = 0;
      } else {
        lastTapRef.current = now;
      }
      // Inicia drag (solo útil cuando hay zoom)
      dragRef.current = { x: e.clientX, y: e.clientY, tx, ty };
    }
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!pointersRef.current.has(e.pointerId)) return;
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointersRef.current.size === 2 && pinchRef.current) {
      movedRef.current = true;
      const pts = Array.from(pointersRef.current.values());
      const dx = pts[0].x - pts[1].x;
      const dy = pts[0].y - pts[1].y;
      const dist = Math.hypot(dx, dy);
      const factor = dist / pinchRef.current.dist;
      const next = Math.max(MIN_SCALE, Math.min(MAX_SCALE, pinchRef.current.scale * factor));
      setScale(next);
      return;
    }

    if (pointersRef.current.size === 1 && dragRef.current && scale > 1) {
      const dx = e.clientX - dragRef.current.x;
      const dy = e.clientY - dragRef.current.y;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) movedRef.current = true;
      setTx(dragRef.current.tx + dx);
      setTy(dragRef.current.ty + dy);
    }
  };

  const onPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size < 2) pinchRef.current = null;
    if (pointersRef.current.size === 0) dragRef.current = null;

    // Si tras un pinch quedamos por debajo de 1, recolocamos a 1 (sin offset)
    if (pointersRef.current.size === 0 && scale <= 1.01 && (tx !== 0 || ty !== 0 || scale !== 1)) {
      reset();
    }
  };

  const onBackgroundClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (movedRef.current) return;
    onClose();
  };

  return (
    <div className="photo-viewer" onClick={onBackgroundClick}>
      <div
        ref={stageRef}
        className="photo-viewer__stage"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <img
          src={src}
          alt="Foto ampliada"
          draggable={false}
          className="photo-viewer__img"
          style={{
            transform: `translate3d(${tx}px, ${ty}px, 0) scale(${scale})`,
            transition: animating ? 'transform 180ms ease-out' : 'none',
          }}
          onTransitionEnd={() => setAnimating(false)}
        />
      </div>
      <button type="button" className="photo-viewer__close" onClick={onClose} aria-label="Cerrar">✕</button>
    </div>
  );
}
